import { Tabs } from '@mantine/core';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getIfcWallEditorPath } from 'app/modules/routePaths';
import { useCopilotRequestSettings } from 'app/modules/wallforming/components/copilot/hooks/useCopilotRequestSettings';
import { GeneratedModelList } from 'app/modules/wallforming/components/plan-to-ifc/GeneratedModelList';
import { usePlanToIfc } from 'app/modules/wallforming/components/plan-to-ifc/hooks/usePlanToIfc';
import { PlanToIfcJobStatus } from 'app/modules/wallforming/components/plan-to-ifc/PlanToIfcJobStatus';
import { PlanToIfcResultCard } from 'app/modules/wallforming/components/plan-to-ifc/PlanToIfcResultCard';
import { PlanUploadForm } from 'app/modules/wallforming/components/plan-to-ifc/PlanUploadForm';

interface PlanToIfcWorkspaceProps {
    /** Called after the user left this view for a forming session or the wall editor (e.g. to close a dialog). */
    onLeave?: () => void;
}

type PlanToIfcTab = 'convert' | 'models';

/**
 * Plan-to-IFC flow shared by the workspace dialog and the start page: one step at a time
 * (upload → progress → result) and stored models on a separate tab.
 */
export const PlanToIfcWorkspace = ({ onLeave }: PlanToIfcWorkspaceProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const requestSettings = useCopilotRequestSettings('plan-to-ifc', 900);
    const {
        result,
        error,
        isGenerating,
        isSubmitting,
        job,
        elapsedSeconds,
        feedbackAgeSeconds,
        generate,
        cancel,
        reset,
        download,
        deleteGeneratedModel,
        isLiveConnected,
        openSession,
        openStoredModel,
        generatedModels,
        isGeneratedModelsPending
    } = usePlanToIfc();
    const [tab, setTab] = useState<PlanToIfcTab>('convert');

    const handleOpenSession = useCallback(
        (sessionId: string) => {
            openSession(sessionId);
            onLeave?.();
        },
        [onLeave, openSession]
    );

    const handleEditWalls = useCallback(
        (ifcModelId: string) => {
            void navigate(getIfcWallEditorPath(ifcModelId));
            onLeave?.();
        },
        [navigate, onLeave]
    );

    const handleOpenStoredModel = useCallback(
        async (ifcModelId: string) => {
            if (await openStoredModel(ifcModelId)) {
                onLeave?.();
            }
        },
        [onLeave, openStoredModel]
    );

    const showForm = !job && !isGenerating;

    return (
        <Tabs value={tab} keepMounted onChange={(value) => setTab((value as PlanToIfcTab | null) ?? 'convert')}>
            <Tabs.List>
                <Tabs.Tab value="convert">{t('tabPlanToIfcConvert')}</Tabs.Tab>
                <Tabs.Tab value="models">{t('tabGeneratedIfcModels', { count: generatedModels.length })}</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel className="space-y-3 pt-3" value="convert">
                {showForm && <div className="text-xs text-text-light">{t('infoPlanToIfc')}</div>}
                {error && (
                    <div
                        className="rounded-sm p-2 text-xs"
                        data-testid="plan-to-ifc-error"
                        style={{ background: '#fef2f2', color: '#b91c1c' }}
                    >
                        <div className="font-medium">{error.detail}</div>
                        {error.errors.length > 0 && (
                            <ul className="mt-1 max-h-28 list-disc overflow-auto pl-4">
                                {error.errors.map((message) => (
                                    <li key={message}>{message}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
                {job && (
                    <PlanToIfcJobStatus
                        elapsedSeconds={elapsedSeconds}
                        feedbackAgeSeconds={feedbackAgeSeconds}
                        isGenerating={isGenerating}
                        isLiveConnected={isLiveConnected}
                        job={job}
                        onCancel={() => void cancel()}
                    />
                )}
                {result && (
                    <PlanToIfcResultCard
                        result={result}
                        onDownload={() => void download(result.ifcModelId, result.fileName)}
                        onEditWalls={() => handleEditWalls(result.ifcModelId)}
                        onNewConversion={reset}
                        onOpenSession={handleOpenSession}
                    />
                )}
                {job && !isGenerating && !result && (
                    <button className="text-xs underline" type="button" onClick={reset}>
                        {t('buttonNewConversion')}
                    </button>
                )}
                {(showForm || isSubmitting) && (
                    <PlanUploadForm
                        isSubmitting={isSubmitting}
                        requestSettings={requestSettings}
                        onGenerate={(name, files, notes, autoForm) =>
                            void generate(name, files, notes, requestSettings.requestOptions, autoForm)
                        }
                    />
                )}
            </Tabs.Panel>

            <Tabs.Panel className="pt-3" value="models">
                <GeneratedModelList
                    isBusy={isGenerating}
                    isPending={isGeneratedModelsPending}
                    models={generatedModels}
                    onDelete={deleteGeneratedModel}
                    onDownload={(model) => void download(model.id, `${model.name}.ifc`)}
                    onEdit={handleEditWalls}
                    onOpen={(ifcModelId) => void handleOpenStoredModel(ifcModelId)}
                />
            </Tabs.Panel>
        </Tabs>
    );
};
